import { useQuery, useMutation, createConnectQueryKey, useTransport } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { create } from '@bufbuild/protobuf'
import { toast } from 'sonner'
import {
  getStripeConnectionStatus,
  getStripeAccountLinkURL,
  disconnectStripeAccount,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  GetStripeConnectionStatusRequestSchema,
} from '@/gen/cortado/v1/payments_pb'

/**
 * Hook to fetch Stripe connection status for an organization
 *
 * This hook:
 * - Fetches the current Stripe connection status for the organization
 * - Returns Stripe account details if connected
 * - Only runs when an organization ID is provided
 * - Uses 1-minute stale time for up-to-date connection status
 *
 * @param organizationId - The organization ID to check Stripe connection for
 * @returns Query result with connection status, account details, loading state, and error
 */
export function useStripeConnection(organizationId: string) {
  const query = useQuery(
    getStripeConnectionStatus,
    create(GetStripeConnectionStatusRequestSchema, { organizationId }),
    {
      enabled: !!organizationId,
      staleTime: 1 * 60 * 1000, // 1 minute - keep fresh for connection status
    }
  )

  return {
    isConnected: query.data?.isConnected || false,
    account: query.data?.account || null,
    isLoading: query.isLoading,
    // An error (e.g. a 403/PermissionDenied) is a terminal, loaded outcome,
    // not an ongoing load - otherwise a caller like the onboarding wizard
    // that gates on `isLoading || !hasLoaded` would stay on its skeleton
    // forever instead of rendering its skip affordance.
    hasLoaded: query.isSuccess || !!query.data || query.isError,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook to get Stripe account link URL and open it in a popup window
 *
 * This mutation:
 * - Requests the account link URL from the backend
 * - Creates Stripe Connected Account if it doesn't exist
 * - Opens the Stripe onboarding URL in a new popup window
 * - Handles success and error states
 *
 * @param organizationId - The organization ID to connect Stripe for
 * @returns Mutation hook with mutate function and state
 */
export function useGetStripeAccountLinkURL(_organizationId: string) {
  return useMutation(getStripeAccountLinkURL, {
    onSuccess: (response) => {
      if (!response.url) {
        toast.error('Unable to open Stripe onboarding', {
          description: 'Stripe did not return an onboarding link. Please try again.',
        })
        return
      }

      // Open Stripe onboarding in popup window
      const width = 800
      const height = 900
      const left = window.screen.width / 2 - width / 2
      const top = window.screen.height / 2 - height / 2

      const popup = window.open(
        response.url,
        'stripe-onboarding',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
      )

      if (!popup) {
        toast.warning('Popup blocked', {
          description: 'Redirecting to Stripe onboarding in this tab.',
        })
        window.location.assign(response.url)
        return
      }

      popup.focus()
    },
    onError: (error) => {
      console.error('Failed to get Stripe account link:', error)
    },
  })
}

/**
 * Hook to disconnect Stripe account from organization
 *
 * This mutation:
 * - Removes the Stripe account ID from the organization
 * - Invalidates the Stripe connection query to refresh the status
 * - Does not delete the actual Stripe account (it still exists in Stripe)
 *
 * @returns Mutation hook with mutate function and state
 */
export function useDisconnectStripeAccount() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  return useMutation(disconnectStripeAccount, {
    onSuccess: () => {
      // Invalidate Stripe connection queries to refetch status
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getStripeConnectionStatus,
          transport,
          cardinality: undefined,
        }),
      })
    },
    onError: (error) => {
      console.error('Failed to disconnect Stripe account:', error)
    },
  })
}
